import {useCallback, useRef} from "react";
import {channel} from "redux-saga";
import {call, select, takeLatest} from "redux-saga/effects";

import {actionEffects as actions} from "../store/actions";
import {useStoreContentList} from "../store";
import {selectContentList} from "../store/selectors";
import {useSagaEffect} from "../utils/sagas";
import {appendUrlParam} from "../utils/url";
import {HOUR_IN_MILLIS} from "../utils/time";
import {apiGet} from "./client";

function* loadFirstPage(key, url) {
    yield actions.contentListLoading(key);
    const {content, has_more_entries: more} = yield call(apiGet, url);
    yield actions.contentListLoaded(key, content, more);
}

function* poll(key, url) {
    yield actions.contentListLoading(key);
    const {content} = yield call(apiGet, url);
    yield actions.contentListPolled(key, content);
}

function* loadNextPage(key, url) {
    const {items} = yield select(selectContentList(key)) || {items: []};
    const counts = {post: 0, comment: 0};
    for (let i = items.length - 1; i >= 0; i--) {
        counts[items[i].type]++;
    }
    if (counts.post) {
        url = appendUrlParam(url, 'post', counts.post);
    }
    if (counts.comment) {
        url = appendUrlParam(url, 'comment', counts.comment);
    }
    url = appendUrlParam(url, 'page', Math.ceil(items.length/25) + 1);
    yield actions.contentListLoading(key);
    try {
        const {content, has_more_entries: more} = yield call(apiGet, url);
        yield actions.contentListExtended(key, content, more);
    } catch {
        yield actions.contentListLoadFailed(key);
    }
}

function* loadContentList(key, url, actionCh) {
    yield takeLatest(actionCh, function* ({type}) {
        switch (type) {
            case 'loadNextPage':
                yield call(loadNextPage, key, url);
                break;
            case 'refresh':
                yield call(loadFirstPage, key, url);
                break;
            case 'poll':
                yield call(poll, key, url);
                break;
            default:
                throw new Error(type);
        }
    });
    yield takeLatest('userChanged', function* () {
        yield call(loadFirstPage, key, url);
    });
    const loaded = Boolean(yield select(selectContentList(key)));
    if (!loaded) {
        yield call(loadFirstPage, key, url);
    }
}

const useContentList = (key, url, feedbackCh, signalRef) => {
    const actionChRef = useRef(null);
    if (!actionChRef.current) {
        actionChRef.current = channel();
    }
    useSagaEffect(feedbackCh, useCallback(function* () {
        yield call(loadContentList, key, url, actionChRef.current, signalRef);
    }, [key, url]));
    const refresh = useCallback(() => actionChRef.current.put({type: 'refresh'}), []);
    const loadNextPage = useCallback(() => actionChRef.current.put({type: 'loadNextPage'}), []);
    const poll = useCallback(() => actionChRef.current.put({ type: 'poll' }), []);
    const list = useStoreContentList(key);

    // Scroll to top and refresh feed if stale.
    if (list.loaded && !list.loading) {
        const now = Date.now();
        if (now > list.timestamp + 6 * HOUR_IN_MILLIS) {
            signalRef.current = 'scrollToTop';
            refresh();
        } else if (!list.polled && Date.now() > list.timestamp + 10000 /* 2 * MINUTE_IN_MILLIS */) {
            poll();
        }
    }

    return {
        ...list,
        refresh: !list.loading && refresh,
        loadNextPage: list.more && !list.loading && loadNextPage
    };
};

export const useProfileOverview = (feedbackCh, username, signalRef) => {
    const key = `u/${username}`;
    const url = `/api/v2/content/profile.json?user=${username}`;
    return useContentList(key, url, feedbackCh, signalRef);
};

export const useQueue = (feedbackCh, community, sort, type, signalRef) => {
    const key = `q:${community}:${sort}:${type}`;
    const url = `/api/v2/moderate/queue.json?community=${community}&sort=${sort}&type=${type}`;
    return useContentList(key, url, feedbackCh, signalRef);
};

export const useSavedContent = (feedbackCh, folder, community, signalRef) => {
    const key = `c/${community}/saved:${folder}`;
    const path = `/api/v2/folder/get_folder.json?community=${community}&folder=${folder}`;
    return useContentList(key, path, feedbackCh, signalRef);
};